import { createSelector } from '@ngrx/store';

import { FeatureFlagKeys } from '../app-entities/feature-flag/feature-flag-state.model';
import { featureFlagSelectors } from '../app-entities/feature-flag/feature-flag-state.selectors';
import { LanguageSelectors } from '../app-global/language/language.selectors';
import { APP_ROUTES } from '../app-global/router/app-routes';

import { selectQuarterlyDiscussionsAvailable } from './quarterly-discussions.selectors';
import { selectCanViewDelegateElevate } from './user-team.selectors';

/** The People section's tabs. `Discussions` is the tab product calls Reviews. */
export enum PeopleTabKey {
  Discussions = 'discussions',
  Assessments = 'assessments',
  DelegateElevate = 'delegate-elevate',
}

export interface PeopleTab {
  key: PeopleTabKey;
  label: string;
  route: string;
}

const peopleRoute = `/${APP_ROUTES.App}/${APP_ROUTES.People}`;

/** Built from the same route tokens `NavigationService` uses, so a rename flows through to both. */
export const PEOPLE_TAB_ROUTES: Record<PeopleTabKey, string> = {
  [PeopleTabKey.Discussions]: `${peopleRoute}/${APP_ROUTES.Discussions}`,
  [PeopleTabKey.Assessments]: `${peopleRoute}/${APP_ROUTES.Assessments}`,
  [PeopleTabKey.DelegateElevate]: `${peopleRoute}/${APP_ROUTES.DelegateElevate}`,
};

/**
 * The People tabs the current user can see, in toolbar order.
 *
 * The single source of truth for tab visibility — the toolbar, the bottom nav bar and the Reviews
 * route guard all read it rather than re-deriving these conditions.
 */
export const selectVisiblePeopleTabs = createSelector(
  LanguageSelectors.language,
  selectQuarterlyDiscussionsAvailable,
  featureFlagSelectors.selectById(FeatureFlagKeys.enableAssessments),
  featureFlagSelectors.selectById(FeatureFlagKeys.enableDelegateElevate),
  selectCanViewDelegateElevate,
  (
    language,
    quarterlyDiscussionsAvailable: boolean,
    enableAssessments: boolean,
    enableDelegateElevate: boolean,
    canViewDelegateElevate: boolean
  ): PeopleTab[] => {
    const tabs: PeopleTab[] = [];

    // Setting only — an unentitled company keeps the tab and gets the upgrade prompt inside it.
    if (quarterlyDiscussionsAvailable) {
      tabs.push({
        key: PeopleTabKey.Discussions,
        label: language?.feedback?.items || 'Discussions',
        route: PEOPLE_TAB_ROUTES[PeopleTabKey.Discussions],
      });
    }

    if (enableAssessments) {
      tabs.push({
        key: PeopleTabKey.Assessments,
        label: language?.survey?.items || 'Assessments',
        route: PEOPLE_TAB_ROUTES[PeopleTabKey.Assessments],
      });
    }

    if (enableDelegateElevate && canViewDelegateElevate) {
      tabs.push({
        key: PeopleTabKey.DelegateElevate,
        label: 'Delegate & Elevate',
        route: PEOPLE_TAB_ROUTES[PeopleTabKey.DelegateElevate],
      });
    }

    return tabs;
  }
);
